import { useNotification } from '@app/AppLayout/NotificationContext';
import { FlagLevelIcon } from '@app/components/FlagLevelIcon';
import { InstructorName } from '@app/components/InstructorName';
import { KlassFlagSummary } from '@app/components/KlassFlagSummary';
import { ClassModalityLabel, ClassStatusLabel, ClassTypeLabel, InstructorNames } from '@app/components/KlassLabels';
import { DELIVERY_RESPONSIBILITY, KLASS_MODALITY, KLASS_STATUS, KLASS_TYPE, REGION } from '@app/model/Enums';
import { FlagLevel } from '@app/model/Flag';
import { Klass, PrivateKlass, resolveInstructors } from '@app/model/Klass';
import { Language } from '@app/model/Language';
import { CALENDAR_WEEK_PAGE_SIZE, CLASS_EDIT_PERIOD_WEEKS, ClassStatusDisplayConfig } from '@app/utils/config';
import { ddMMyyyyHHmmTzFormat, displayAddress, fromIsoDate, toIsoDate, weekNumber } from '@app/utils/functions';
import {
  AlertVariant,
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  Content,
  Divider,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Popover,
  SearchInput,
  Select,
  SelectOption,
  Switch,
  TextInputGroup,
  TextInputGroupMain,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
  ToolbarContent,
  ToolbarFilter,
  ToolbarGroup,
  ToolbarItem,
  ToolbarLabel,
  ToolbarLabelGroup,
  ToolbarToggleGroup
} from '@patternfly/react-core';
import { AngleLeftIcon, AngleRightIcon, FilterIcon, TimesIcon, UmbrellaIcon } from '@patternfly/react-icons';
import { PageActionsMenu, PageActionsMenuItem } from '@app/components/PageActionsMenu';
import { hasRealmRole } from '@app/utils/keycloak';
import { ROLE } from '@app/utils/roles';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HolidayApi, KlassApi } from '@app/api';
import { useEntity } from '@app/hooks/useEntity';
import { hydrateNationwideHolidays, useCountryStore, useHolidayStore, useInstructorStore, useKlassStore, usePartnerStore } from '@app/store/entityStore';

type ViewMode = 'week' | 'month';

// --- Default selected countries ---
const DEFAULT_HOLIDAY_COUNTRIES_ALPHA2 = ['GB', 'ES', 'NL', 'DE'];

/**
 * The filter categories, each against the field it writes to in `filters`.
 *
 * Same arrangement as the classes overview: one dropbox picks the category, and the control for
 * that category is the only one on screen. The field names are the overview's too, so a reader
 * moving between the two pages meets the same predicates.
 */
const CATEGORY_MAP: Record<string, string> = {
  'Instructor': 'instructor',
  'Partner': 'partner',
  'Course Name/SKU': 'name',
  'Language': 'language',
  'Region': 'region'
};

/** The empty filter set: an entry per filterable field, none of them selected. */
const emptyCalendarFilters = (): Record<string, string[]> => ({
  instructor: [],
  partner: [],
  name: [],
  language: [],
  region: []
});

/**
 * Stands for "nobody is teaching this yet" in the instructor list, so an unallocated class can be
 * filtered for. Not a name, so it cannot collide with one.
 */
const NO_INSTRUCTOR = '--- NO INSTRUCTOR ---';

/**
 * The ISO week a Sun-Sat row of this calendar is labelled with.
 *
 * The grid runs Sun-Sat so that a class starting on a Sunday stays on one line, but ISO weeks run
 * Mon-Sun, so each row straddles two of them: the opening Sunday closes one week and the Mon-Sat
 * that follow open the next. Six days out of seven decide it, so the row takes the number of the
 * Monday it holds. Reading the ISO week off the row's Sunday instead - which is what this used to
 * do - names the week that has just ended and leaves every label one short.
 */
const columnWeekNumber = (columnStart: Date) => {
  const monday = new Date(columnStart);
  monday.setDate(columnStart.getDate() + 1);
  return weekNumber(monday);
};

/** The calendar's own dates are built at local midnight, so they have to be read off the
 * local calendar too - toISOString() would name the day before for a positive offset. */
const formatApiDate = (date: Date): string => toIsoDate(date);

const getWeekendDays = (countryCode?: string): number[] => {
  const code = countryCode?.toUpperCase();
  const friSatAlpha2 = ['DZ', 'BH', 'EG', 'IQ', 'IL', 'JO', 'KW', 'LY', 'OM', 'PS', 'QA', 'SA', 'SD', 'SY', 'YE', 'BD', 'MV'];
  const friSatAlpha3 = ['DZA', 'BHR', 'EGY', 'IRQ', 'ISR', 'JOR', 'KWT', 'LBY', 'OMN', 'PSE', 'QAT', 'SAU', 'SDN', 'SYR', 'YEM', 'BGD', 'MDV'];
  if (code && (friSatAlpha2.includes(code) || friSatAlpha3.includes(code))) return [5, 6]; 
  return [0, 6]; 
};

// --- Extracted Event Component ---
const ClassEventItem: React.FC<{ klass: Klass; isCompactMonth?: boolean }> = ({ klass, isCompactMonth = false }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  // Decorated with each instructor's certification for the class's course, as everywhere else a
  // name is shown. The chip is itself a popover trigger, so the explanation goes on as a plain
  // title rather than a PatternFly tooltip, which would compete with the popover for the pointer,
  // and the link to the instructor's page is left to the panel the chip opens - a chip is 22px
  // tall and its whole surface is already a click that opens the details.
  const resolvedInstructors = resolveInstructors(klass);
  const instructorName = resolvedInstructors.length
    ? resolvedInstructors.map((instructor, index) => (
        <React.Fragment key={instructor.id ?? instructor.name}>
          {index > 0 && ', '}
          <InstructorName instructor={instructor} course={klass.course} nativeTooltip hideDetailLink />
        </React.Fragment>
      ))
    : 'no instructor';

  const statusKey = klass.status as string;
  const colors = ClassStatusDisplayConfig[statusKey] || ClassStatusDisplayConfig.DEFAULT;
  const hasCriticalFlag = klass.activeFlags?.some(f => f.flagLevel === FlagLevel.CRITICAL);
  
  const hoverDetails = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
      <div><strong>OID:</strong> {klass.offeringId}</div>
      <div><strong>Course:</strong> {klass.course?.name}</div>
      {/* The same names as on the chip, but here each carries a link to the instructor's own page
          and a PatternFly tooltip over the certification icon - see InstructorNames. */}
      <div>
        <strong>Instructor{resolvedInstructors.length > 1 ? 's' : ''}:</strong>{' '}
        <InstructorNames instructors={resolvedInstructors} course={klass.course} />
      </div>
      <div><strong>Start Date:</strong> {ddMMyyyyHHmmTzFormat(klass.startDateTime)}</div>
      <div><strong>End Date:</strong> {ddMMyyyyHHmmTzFormat(klass.endDateTime)}</div>
      <div><strong>Type:</strong> <ClassTypeLabel type={klass.type} /></div>
      <div><strong>Modality:</strong> <ClassModalityLabel modality={klass.modality} /></div>
      <div><strong>Status:</strong> <ClassStatusLabel status={klass.status} /></div>
      {klass.modality !== KLASS_MODALITY.VIRTUAL && (
        <div>
          <strong>Address:</strong>{' '}
          { klass.type === KLASS_TYPE.PRIVATE && !klass.facility ? displayAddress((klass as PrivateKlass).deliveryAddress) : klass.facility ? klass.facility.name + ' ' + displayAddress(klass.facility.address) : 'TBD' }
        </div>
      )}
      <details style={{ cursor: 'pointer' }}>
        <summary style={{ fontWeight: 'bold', outline: 'none' }}>
          Enrolments: {klass.totalEnrolled || 0} / {klass.maxDelegates || 0} (Details &#8595;)
        </summary>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          columnGap: '16px', 
          rowGap: '4px',
          fontSize: '12px',
          marginTop: '8px',
          padding: '8px',
          backgroundColor: 'var(--pf-t--global--background--color--secondary--default, #f0f0f0)',
          borderRadius: '4px'
        }}>
          <div>Paying: {klass.payingDelegates || 0}</div>
          <div>Free: {klass.freeDelegates || 0}</div>
          <div>Premium: {klass.rhlsPremiumDelegates || 0}</div>
          <div>Internal: {klass.internalDelegates || 0}</div>
          <div>RHLS Crse: {klass.rhlsCourseDelegates || 0}</div>
          <div>Bundled: {klass.bundledDelegates || 0}</div>
          <div>T. Units: {klass.trainingUnitsDelegates || 0}</div>
          <div>Booked: <strong>{klass.delegatesBooked || 0}</strong></div>
        </div>
      </details>
      <div><strong>Flags:</strong> <KlassFlagSummary flags={klass.activeFlags} /></div>
      <div style={{ alignSelf: 'flex-end', marginTop: '4px' }}>
        <Button variant="link" isInline onClick={() => navigate('/classes/' + klass.offeringId)}>View class details</Button>
      </div>
    </div>
  );

  return (
    <Popover key={klass.id} headerContent={<div>{klass.course?.sku} Details</div>} bodyContent={hoverDetails} position="auto">
      <div 
        tabIndex={0}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backgroundColor: colors!.bg,
          color: colors!.text,
          padding: isCompactMonth ? '2px 6px' : '6px 8px',
          borderRadius: '4px',
          fontSize: isCompactMonth ? '11px' : '12px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: isCompactMonth ? 'row' : 'column',
          alignItems: isCompactMonth ? 'center' : 'flex-start',
          gap: '4px',
          minWidth: 0,
          height: isCompactMonth ? '22px' : 'auto', 
          borderLeft: hasCriticalFlag ? '3px solid var(--pf-t--global--color--status--danger--default, #c9190b)' : '3px solid var(--pf-t--global--color--brand--default, #0066cc)',
          
          outline: isHovered ? '2px solid var(--pf-t--global--color--brand--default, #0066cc)' : 'none',
          outlineOffset: '-1px',
          boxShadow: isHovered ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
          transform: isHovered ? 'translateY(-1px)' : 'none',
          transition: 'all 0.15s ease-in-out',
          zIndex: isHovered ? 50 : 'auto', 
          position: 'relative'
        }}
      >
        <div style={{ 
          fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%',
          textDecoration: klass.status === KLASS_STATUS.CANCELLED ? 'line-through' : 'none',
          opacity: klass.status === KLASS_STATUS.CANCELLED ? 0.7 : 1 
         }}>
          { hasCriticalFlag && <><FlagLevelIcon level={FlagLevel.CRITICAL} label="Has a critical flag" style={{marginRight: '4px', verticalAlign: 'text-bottom'}} />&nbsp;</>}
          {klass.course?.sku} {isCompactMonth ? <span style={{fontWeight: 'normal'}}> | {instructorName}</span> : <> {' | '} {instructorName} </>}
        </div>
      </div>
    </Popover>
  );
};

export const Calendar: React.FC = () => {
  const WEEKS_TO_SHOW = 7; 
  const MAX_MONTH_TRACKS = 4; 

  const { addNotification } = useNotification();

  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('week'); 
  const [expandedWeekIndex, setExpandedWeekIndex] = useState<number | null>(null);
  
  const [hideCancelled, setHideCancelled] = useState<boolean>(false);

  /**
   * Whether the non-NIIT deliveries are the user's to ask for, as on the classes overview.
   * Everybody else works the classes this team delivers, so for them the toggle is not offered
   * and the calendar stays as it opens.
   */
  const isAdmin = hasRealmRole(ROLE.ADMIN);

  /**
   * Starts on, so the calendar opens on the classes this team delivers. The ones Red Hat or a
   * third party delivers are still there to be asked for, but they are not what the page is for.
   */
  const [hideNonNiitDelivery, setHideNonNiitDelivery] = useState<boolean>(true);

  // --- Filter State ---
  // One record for every filterable field, whichever control wrote to it - see CATEGORY_MAP.
  const [filters, setFilters] = useState<Record<string, string[]>>(emptyCalendarFilters);
  const [currentCategory, setCurrentCategory] = useState<string>('Instructor');

  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isInstructorDropdownOpen, setIsInstructorDropdownOpen] = useState(false);
  const [isPartnerDropdownOpen, setIsPartnerDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);

  // Each long option list narrows on its own search box; `skuInput` is the term being typed
  // into the Course Name/SKU box, before it is entered and becomes a chip.
  const [instSearch, setInstSearch] = useState('');
  const [partnerSearch, setPartnerSearch] = useState('');
  const [langSearch, setLangSearch] = useState('');
  const [skuInput, setSkuInput] = useState('');
  
  // Entities
  const { items: classes, retry } = useEntity(useKlassStore);
  const { items: instructors } = useEntity(useInstructorStore);
  const { items: partners } = useEntity(usePartnerStore);
  const { items: countries } = useEntity(useCountryStore);
  const { items: allHolidays } = useEntity(useHolidayStore);

  // --- Local Historical Data Management ---
  const [localHistoricalClasses, setLocalHistoricalClasses] = useState<Klass[]>([]);
  const fetchedHistoricalWeeksRef = React.useRef(new Set<string>());

  // --- Holiday State ---
  const [selectedHolidayCountries, setSelectedHolidayCountries] = useState<string[]>([]);
  const fetchedHolidaysRef = React.useRef(new Set<string>());
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [modalSelectedCountries, setModalSelectedCountries] = useState<string[]>([]);
  const [holidaySearch, setHolidaySearch] = useState('');

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // --- Initialize Default Holiday Countries ---
  const initializedDefaults = React.useRef(false);

  useEffect(() => {
    // Wait until the store has loaded the countries, and only run this once
    if (countries.length > 0 && !initializedDefaults.current) {
      const defaultCountryIds = countries
        .filter(c => c.alpha2 && DEFAULT_HOLIDAY_COUNTRIES_ALPHA2.includes(c.alpha2.toUpperCase()))
        .map(c => c.id.toString());

      setSelectedHolidayCountries(defaultCountryIds);
      initializedDefaults.current = true;
    }
  }, [countries]);

  const instructorFilterOptions = useMemo(() => {
    const names = instructors.map(i => i.name).filter(Boolean);
    const sortedNames = Array.from(new Set<string>(names)).sort((a, b) => a.localeCompare(b));
    return [NO_INSTRUCTOR, ...sortedNames];
  }, [instructors]);

  const partnerFilterOptions = useMemo(() => {
    const names = partners.map(p => p.name).filter(Boolean);
    return Array.from(new Set<string>(names)).sort((a, b) => a.localeCompare(b));
  }, [partners]);

  const visibleRange = useMemo<{ start: Date; end: Date }>(() => {
    let start: Date;
    let end: Date;

    if (viewMode === 'month') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      start = new Date(year, month, 1);
      start.setDate(start.getDate() - start.getDay() - 7); 
      end = new Date(year, month + 1, 0); 
      end.setDate(end.getDate() + (6 - end.getDay()) + 7); 
      end.setHours(23, 59, 59, 999);
    } else {
      start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay() - 7); 
      end = new Date(start);
      end.setDate(start.getDate() + (WEEKS_TO_SHOW * 7) - 1);
      end.setHours(23, 59, 59, 999);
    }
    return { start, end };
  }, [currentDate, viewMode]);

  // Fetch new holidays and merge them into the global cache
  useEffect(() => {
    let isMounted = true;

    const loadNewHolidays = async () => {
      const years = Array.from(new Set([visibleRange.start.getFullYear(), visibleRange.end.getFullYear()]));
      
      for (const countryId of selectedHolidayCountries) {
        for (const year of years) {
          const queryKey = `${countryId}-${year}`;
          
          if (!fetchedHolidaysRef.current.has(queryKey)) {
            try {
              fetchedHolidaysRef.current.add(queryKey);

              // No region asked for, so the service answers with the days the country as a
              // whole observes - which is what an overlay spanning several countries should
              // draw. Asking for every subdivision's would take Spain alone from about a
              // dozen days a year to near fifty, most of them not observed where the reader is.
              const res = await HolidayApi.getByCountryAndYear(countryId, year);
              const data = Array.isArray(res) ? res : res.content || [];
              const processedHolidays = hydrateNationwideHolidays(data);
              
              if (isMounted && processedHolidays.length > 0) {
                const currentStore = useHolidayStore.getState();
                const existingIds = new Set(currentStore.items.map(h => h.id));
                const newItems = processedHolidays.filter(h => !existingIds.has(h.id));
                
                if (newItems.length > 0) {
                  currentStore.setItems([...currentStore.items, ...newItems]);
                }
              }
            } catch (error: any) {
              fetchedHolidaysRef.current.delete(queryKey);
              addNotification(`Error fetching holidays for country ${countryId}`, AlertVariant.danger, error.message);
            }
          }
        }
      }
    };

    loadNewHolidays();
    return () => { isMounted = false; };
  }, [selectedHolidayCountries, visibleRange]);

  useEffect(() => {
    let isMounted = true;
    
    // The Sunday opening the store's window. Weeks before it are not in the cache and
    // have to come from the API, so this has to be the same period the store fetches.
    const globalCutoff = new Date();
    globalCutoff.setDate(globalCutoff.getDate() - globalCutoff.getDay() - (CLASS_EDIT_PERIOD_WEEKS * 7));
    globalCutoff.setHours(0, 0, 0, 0);

    const loadHistoricalWeeks = async () => {
      const weeksToFetch: Date[] = [];
      const curr = new Date(visibleRange.start);
      curr.setDate(curr.getDate() - curr.getDay()); // Align strictly to Sunday
      curr.setHours(0, 0, 0, 0);

      // Identify which weeks in the visible range are BEFORE the global cache cutoff
      while (curr <= visibleRange.end && curr < globalCutoff) {
        const key = formatApiDate(curr);
        if (!fetchedHistoricalWeeksRef.current.has(key)) {
          weeksToFetch.push(new Date(curr));
          fetchedHistoricalWeeksRef.current.add(key);
        }
        curr.setDate(curr.getDate() + 7);
      }

      if (weeksToFetch.length === 0) return;

      try {
        // Fetch each missing week directly from the API (bypassing the store)
        const promises = weeksToFetch.map(weekStart => {
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);
          
          return KlassApi.getAll({
            startDate: formatApiDate(weekStart),
            endDate: formatApiDate(weekEnd),
            size: CALENDAR_WEEK_PAGE_SIZE
          });
        });

        const results = await Promise.all(promises);
        const newClasses = results.flat();

        // Merge locally, ensuring we don't duplicate any edge cases
        if (isMounted && newClasses.length > 0) {
          setLocalHistoricalClasses(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const uniqueNew = newClasses.filter(c => !existingIds.has(c.id));
            return [...prev, ...uniqueNew];
          });
        }
      } catch (error: any) {
        // Revert the tracking ref so it can try again if the user navigates back
        weeksToFetch.forEach(w => fetchedHistoricalWeeksRef.current.delete(formatApiDate(w)));
        if (isMounted) addNotification("Error loading historical classes", AlertVariant.danger, error.message);
      }
    };

    loadHistoricalWeeks();
    return () => { isMounted = false; };
  }, [visibleRange]);

  // Flatten active holidays for fast rendering
  // Instantly filters the global cache based on selected countries
  const activeHolidays = useMemo(() => {
    return allHolidays.filter(h => 
      h.country?.id && selectedHolidayCountries.includes(h.country.id.toString())
    );
  }, [allHolidays, selectedHolidayCountries]);

  // --- Rest of original code ---
  const weekColumns = useMemo(() => {
    const columns: { start: Date; end: Date; weekNum: number }[] = [];
    for (let w = 0; w < WEEKS_TO_SHOW; w++) {
      const start = new Date(visibleRange.start);
      start.setDate(visibleRange.start.getDate() + (w * 7));
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      columns.push({ start, end, weekNum: columnWeekNumber(start) });
    }
    return columns;
  }, [visibleRange]);

  const monthWeeks = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const start = new Date(year, month, 1);
    start.setDate(start.getDate() - start.getDay()); 
    const end = new Date(year, month + 1, 0);
    end.setDate(end.getDate() + (6 - end.getDay())); 
    
    const weeks: Date[][] = [];
    const current = new Date(start);
    while (current <= end) {
      const week: Date[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }, [currentDate]);

  /** Adds or removes one value from a field - what both the checkbox lists and the chips do. */
  const toggleFilterValue = (field: string, value: string) => setFilters(prev => {
    const current = prev[field] ?? [];
    return { ...prev, [field]: current.includes(value) ? current.filter(v => v !== value) : [...current, value] };
  });

  /**
   * A term entered in the Course Name/SKU box.
   *
   * Held as a chip rather than filtering as it is typed, so several terms can be searched at once
   * and each one stays visible and removable on its own.
   */
  const onTextSearch = (value: string) => {
    const term = value.trim();
    const field = CATEGORY_MAP[currentCategory];
    if (!term || !field) return;
    if (!(filters[field] ?? []).includes(term)) {
      setFilters({ ...filters, [field]: [...(filters[field] ?? []), term] });
    }
    setSkuInput('');
  };

  const onDeleteFilter = (category: string | ToolbarLabelGroup, id: string | ToolbarLabel) => {
    const field = CATEGORY_MAP[typeof category === 'string' ? category : category.name];
    if (!field) return;
    const value = typeof id === 'string' ? id : String(id.key);
    setFilters(prev => ({ ...prev, [field]: (prev[field] ?? []).filter(v => v !== value) }));
  };

  // Leaves Hide Cancelled where it is: it is a display toggle rather than one of the filters
  // hanging off the toolbar, so clearing chips that never included it must not silently move it.
  const clearAllFilters = () => {
    setFilters(emptyCalendarFilters());
    setInstSearch(''); setPartnerSearch(''); setLangSearch(''); setSkuInput('');
  };

  // Holiday countries are picked in a modal and synced on close, not toggled one at a time.

  /**
   * Every class the calendar knows of: the global cache plus the historical weeks this page has
   * fetched for itself, deduplicated by id.
   *
   * Kept apart from the filtering below so the language options can be drawn from the whole set
   * rather than from whatever the current filters have left on screen.
   */
  const combinedClasses = useMemo(() => {
    const allMap = new Map<string | number, Klass>();
    classes.forEach(c => allMap.set(c.id, c));
    localHistoricalClasses.forEach(c => allMap.set(c.id, c));
    return Array.from(allMap.values());
  }, [classes, localHistoricalClasses]);

  // Gathered from the classes rather than from the language store, as the overview gathers them:
  // an option matching nothing the calendar holds is an option that can only empty the view.
  const availableLanguages = useMemo(() => {
    const langMap = new Map<string, Language>();
    combinedClasses.forEach(c => { if (c.language?.englishName) langMap.set(c.language.englishName, c.language); });
    return Array.from(langMap.values()).sort((a, b) => a.englishName.localeCompare(b.englishName));
  }, [combinedClasses]);

  const displayedClasses = useMemo(() => {
    const { instructor = [], partner = [], name = [], language = [], region = [] } = filters;

    return combinedClasses.filter(c => {
      if (hideCancelled && c.status === KLASS_STATUS.CANCELLED) return false;

      // Only the classes named to a third party are out while the toggle is on. A Red Hat
      // delivery stays, as does a class carrying no delivery responsibility at all: neither is
      // known to be somebody else's, and hiding either would take classes off the page silently.
      if (hideNonNiitDelivery && c.deliveryResponsibility === DELIVERY_RESPONSIBILITY.NON_NIIT) return false;
      
      // A class delivered by several instructors is kept when any one of them is picked,
      // so filtering on one member of a pair does not hide the class from the other.
      const resolved = resolveInstructors(c);
      const instNames = resolved.length ? resolved.map(i => i.name) : [NO_INSTRUCTOR];
      const partnerNames = resolved.map(i => i.partner?.name).filter(Boolean) as string[];

      if (instructor.length > 0 && !instNames.some(n => instructor.includes(n))) return false;
      if (partner.length > 0 && !partnerNames.some(p => partner.includes(p))) return false;

      // Course name and SKU are searched together on any part of either, so "rh124" and
      // "System Administration" both find the same classes. Terms are an OR, as the overview
      // reads them: each one added widens the view rather than narrowing it.
      if (name.length > 0 && !name.some(term => {
        const search = term.toLowerCase();
        return c.course?.name?.toLowerCase().includes(search) || c.course?.sku?.toLowerCase().includes(search);
      })) return false;

      if (language.length > 0 && !language.includes(c.language?.englishName ?? '')) return false;

      // A class the report named no operating unit for matches no region, rather than matching
      // every one of them: filtering on EMEA must not turn up the classes whose unit is unknown.
      if (region.length > 0 && !region.includes(c.region as string)) return false;

      return true;
    });
  }, [combinedClasses, hideCancelled, hideNonNiitDelivery, filters]);

  const multiWeekData = useMemo(() => {
    if (viewMode !== 'week' || weekColumns.length === 0) return { placements: [], displayTracks: 15 };

    const globalStart = weekColumns[0]!.start;
    const globalEnd = weekColumns[weekColumns.length - 1]!.end;

    const visibleEvents = displayedClasses.filter(c => {
      const s = new Date(c.startDateTime);
      const e = c.endDateTime ? new Date(c.endDateTime) : s;
      return s <= globalEnd && e >= globalStart;
    }).sort((a, b) => {
      const aS = new Date(a.startDateTime);
      const aE = a.endDateTime ? new Date(a.endDateTime) : aS;
      const bS = new Date(b.startDateTime);
      const bE = b.endDateTime ? new Date(b.endDateTime) : bS;
      const aDur = aE.getTime() - aS.getTime();
      const bDur = bE.getTime() - bS.getTime();
      if (bDur !== aDur) return bDur - aDur;
      return aS.getTime() - bS.getTime();
    });

    let requiredTracks = 0;
    const trackOccupancy: boolean[][] = [];
    const placements: { klass: Klass, track: number, startCol: number, endCol: number }[] = [];

    visibleEvents.forEach(klass => {
      const kS = new Date(klass.startDateTime);
      const kE = klass.endDateTime ? new Date(klass.endDateTime) : kS;
      let startCol = -1;
      let endCol = -1;

      weekColumns.forEach((w, i) => {
        if (kS <= w.end && kE >= w.start) {
          if (startCol === -1) startCol = i;
          endCol = i;
        }
      });

      if (startCol === -1) return;

      let assignedTrack = 0;
      for (;;) {
        if (!trackOccupancy[assignedTrack]) {
          trackOccupancy[assignedTrack] = Array(weekColumns.length).fill(false);
        }
        let free = true;
        for (let c = startCol; c <= endCol; c++) {
          if (trackOccupancy[assignedTrack]![c]) { free = false; break; }
        }
        if (free) {
          for (let c = startCol; c <= endCol; c++) {
            trackOccupancy[assignedTrack]![c] = true;
          }
          if (assignedTrack + 1 > requiredTracks) requiredTracks = assignedTrack + 1;
          break;
        }
        assignedTrack++;
      }
      placements.push({ klass, track: assignedTrack, startCol, endCol });
    });

    return { placements, displayTracks: Math.max(requiredTracks, 15) };
  }, [displayedClasses, weekColumns, viewMode]);
  
  const handlePrev = () => {
    if (viewMode === 'month') setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    else setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - (WEEKS_TO_SHOW * 7)));
  };

  const handleNext = () => {
    if (viewMode === 'month') setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    else setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + (WEEKS_TO_SHOW * 7)));
  };

  const handlePrevWeek = () => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 7));
  const handleNextWeek = () => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 7));
  const handleToday = () => setCurrentDate(new Date());

  // --- Filter Toolbar ---

  /** The dropbox that decides which filter control is on screen. */
  const buildCategoryDropdown = () => (
    <ToolbarItem>
      <Select
        aria-label="Filter category"
        onSelect={(_e, val) => { setCurrentCategory(val as string); setIsCategoryDropdownOpen(false); setSkuInput(''); }}
        selected={currentCategory}
        isOpen={isCategoryDropdownOpen}
        onOpenChange={(isOpen) => setIsCategoryDropdownOpen(isOpen)}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
            isExpanded={isCategoryDropdownOpen} icon={<FilterIcon />} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            {currentCategory}
          </MenuToggle>
        )}
      >
        {Object.keys(CATEGORY_MAP).map(category => (
          <SelectOption key={category} value={category}>{category}</SelectOption>
        ))}
      </Select>
    </ToolbarItem>
  );

  /**
   * A category filtered by picking from a list: every option a checkbox, so more than one can be
   * on at once, and each selection hung off the toolbar as its own removable chip.
   *
   * `search` is left off a list short enough to read at a glance - Region has four entries, and a
   * search box over it would only be in the way.
   */
  const renderCheckboxFilter = (opts: {
    categoryName: string;
    field: string;
    toggleText: string;
    options: { key: string; value: string }[];
    isOpen: boolean;
    setOpen: (isOpen: boolean) => void;
    minWidth: string;
    search?: { value: string; setValue: (value: string) => void; placeholder: string };
  }) => {
    const selected = filters[opts.field] ?? [];
    const term = opts.search?.value.toLowerCase() ?? '';
    const visible = term ? opts.options.filter(o => o.value.toLowerCase().includes(term)) : opts.options;

    // Built as a flat list rather than nested in a fragment: the Select lays its children out as
    // one menu, and the search box has to sit in it beside the options.
    const menuItems: React.ReactNode[] = [];
    if (opts.search) {
      const { value, setValue, placeholder } = opts.search;
      menuItems.push(
        <div key="search" style={{ padding: '8px' }}>
          <SearchInput value={value} onChange={(_e, val) => setValue(val)} onClear={() => setValue('')}
            placeholder={placeholder} aria-label={placeholder} />
        </div>,
        <Divider key="search-divider" />
      );
    }
    visible.forEach(option => menuItems.push(
      <SelectOption hasCheckbox key={option.key} value={option.value} isSelected={selected.includes(option.value)}>
        {option.value}
      </SelectOption>
    ));
    if (visible.length === 0) {
      menuItems.push(<SelectOption key="no-results" isDisabled value="No results">No results found</SelectOption>);
    }

    return (
      <ToolbarFilter key={opts.categoryName} labels={selected} deleteLabel={onDeleteFilter}
        categoryName={opts.categoryName} showToolbarItem={currentCategory === opts.categoryName}>
        <Select
          aria-label={opts.categoryName}
          isOpen={opts.isOpen}
          onOpenChange={opts.setOpen}
          popperProps={{ minWidth: opts.minWidth }}
          onSelect={(_e, value) => toggleFilterValue(opts.field, String(value))}
          selected={selected}
          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
            <MenuToggle ref={toggleRef} onClick={() => opts.setOpen(!opts.isOpen)} isExpanded={opts.isOpen}
              style={{ width: '100%', verticalAlign: 'text-bottom' }}>
              {opts.toggleText} {selected.length > 0 && <Badge isRead>{selected.length}</Badge>}
            </MenuToggle>
          )}
        >
          {menuItems}
        </Select>
      </ToolbarFilter>
    );
  };

  /**
   * A category filtered by typing. A search rather than a dropdown because the courses run to
   * thousands, and because a partial SKU or a few words of a title is what anyone has to hand.
   */
  const renderTextFilter = (categoryName: string, field: string) => (
    <ToolbarFilter key={categoryName} labels={filters[field] ?? []} deleteLabel={onDeleteFilter}
      categoryName={categoryName} showToolbarItem={currentCategory === categoryName}>
      <SearchInput
        aria-label={`${categoryName} filter`}
        placeholder={`Filter by ${categoryName}...`}
        value={skuInput}
        onChange={(_e, val) => setSkuInput(val)}
        onClear={() => setSkuInput('')}
        onSearch={(_e, value) => onTextSearch(value)}
      />
    </ToolbarFilter>
  );

  const buildFilterControls = () => (
    <React.Fragment>
      {renderCheckboxFilter({
        categoryName: 'Instructor', field: 'instructor', toggleText: 'Filter by instructor',
        options: instructorFilterOptions.map(name => ({ key: name, value: name })),
        isOpen: isInstructorDropdownOpen, setOpen: setIsInstructorDropdownOpen, minWidth: '250px',
        search: { value: instSearch, setValue: setInstSearch, placeholder: 'Search instructors...' }
      })}

      {renderCheckboxFilter({
        categoryName: 'Partner', field: 'partner', toggleText: 'Filter by partner',
        options: partnerFilterOptions.map(name => ({ key: name, value: name })),
        isOpen: isPartnerDropdownOpen, setOpen: setIsPartnerDropdownOpen, minWidth: '250px',
        search: { value: partnerSearch, setValue: setPartnerSearch, placeholder: 'Search partners...' }
      })}

      {renderTextFilter('Course Name/SKU', 'name')}

      {renderCheckboxFilter({
        categoryName: 'Language', field: 'language', toggleText: 'Filter by language',
        options: availableLanguages.map(lang => ({ key: lang.code, value: lang.englishName })),
        isOpen: isLanguageDropdownOpen, setOpen: setIsLanguageDropdownOpen, minWidth: '200px',
        search: { value: langSearch, setValue: setLangSearch, placeholder: 'Search languages...' }
      })}

      {/* Straight off the enum rather than gathered from the classes, unlike Language: there are
          four operating units and they do not change, so all four stay offered even when the
          weeks on screen happen to hold classes from only one. */}
      {renderCheckboxFilter({
        categoryName: 'Region', field: 'region', toggleText: 'Filter by region',
        options: Object.values(REGION).map(region => ({ key: region, value: region })),
        isOpen: isRegionDropdownOpen, setOpen: setIsRegionDropdownOpen, minWidth: '150px'
      })}
    </React.Fragment>
  );

  const renderMonthWeekRow = (week: Date[], idx: number) => {
    const weekStart = week[0];
    const weekEnd = new Date(week[6]!);
    weekEnd.setHours(23, 59, 59, 999);

    const isExpanded = expandedWeekIndex === idx;

    const weekEvents = displayedClasses.filter(c => {
      const s = new Date(c.startDateTime);
      const e = c.endDateTime ? new Date(c.endDateTime) : s;
      return s <= weekEnd && e >= weekStart!;
    }).sort((a, b) => {
      const aS = new Date(a.startDateTime);
      const aE = a.endDateTime ? new Date(a.endDateTime) : aS;
      const bS = new Date(b.startDateTime);
      const bE = b.endDateTime ? new Date(b.endDateTime) : bS;
      const aDur = aE.getTime() - aS.getTime();
      const bDur = bE.getTime() - bS.getTime();
      if (bDur !== aDur) return bDur - aDur;
      return aS.getTime() - bS.getTime();
    });

    let requiredTracks = 0;
    const trackOccupancy: boolean[][] = []; 
    const eventPlacements: { klass: Klass, track: number, startCol: number, endCol: number }[] = [];

    weekEvents.forEach(klass => {
      const s = new Date(klass.startDateTime);
      const e = klass.endDateTime ? new Date(klass.endDateTime) : s;

      const startCol = s < weekStart! ? 0 : s.getDay();
      const endCol = e > weekEnd! ? 6 : e.getDay();

      const countryCode = (klass as any).country?.alpha2 || (klass as any).country?.alpha3;
      const weekendDays = getWeekendDays(countryCode);

      let activeStartCol = startCol;
      while (activeStartCol <= endCol && weekendDays.includes(activeStartCol)) activeStartCol++;
      
      let activeEndCol = endCol;
      while (activeEndCol >= activeStartCol && weekendDays.includes(activeEndCol)) activeEndCol--;
      
      if (activeStartCol > activeEndCol) return; 

      let assignedTrack = 0;
      for (;;) {
        if (!trackOccupancy[assignedTrack]) {
          trackOccupancy[assignedTrack] = Array(7).fill(false);
        }
        let free = true;
        for (let c = activeStartCol; c <= activeEndCol; c++) {
          if (trackOccupancy[assignedTrack]![c]) { free = false; break; }
        }
        if (free) {
          for (let c = activeStartCol; c <= activeEndCol; c++) {
            trackOccupancy[assignedTrack]![c] = true;
          }
          if (assignedTrack + 1 > requiredTracks) {
            requiredTracks = assignedTrack + 1;
          }
          break; 
        }
        assignedTrack++;
      }
      eventPlacements.push({ klass, track: assignedTrack, startCol: activeStartCol, endCol: activeEndCol });
    });

    const displayTracks = isExpanded ? Math.max(requiredTracks, MAX_MONTH_TRACKS) : MAX_MONTH_TRACKS;
    const placedEvents = eventPlacements.filter(ep => ep.track < displayTracks);

    const hiddenCount = eventPlacements.length - placedEvents.length;
    const showFooterRow = isExpanded || hiddenCount > 0;
    const totalGridRows = displayTracks + (showFooterRow ? 2 : 1); 

    return (
      <div key={`month-week-${idx}`} style={{ display: 'grid', gridTemplateColumns: '40px repeat(7, 1fr)', gridTemplateRows: `28px repeat(${displayTracks}, 26px) ${showFooterRow ? 'minmax(24px, auto)' : ''}`, backgroundColor: 'var(--pf-t--global--background--color--primary--default, #fff)', borderBottom: '1px solid var(--pf-t--global--border--color--default, #d2d2d2)' }}>
        <div
          style={{ gridColumn: 1, gridRow: `1 / ${totalGridRows + 1}`, backgroundColor: 'var(--pf-t--global--background--color--secondary--default, #f0f0f0)', borderRight: '1px solid var(--pf-t--global--border--color--default, #d2d2d2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: 'var(--pf-t--global--text--color--subtle, #6a6e73)', cursor: 'pointer' }}
          onClick={() => setExpandedWeekIndex(isExpanded ? null : idx)}
        >
          {columnWeekNumber(weekStart!)}
        </div>

        {week.map((day, dIdx) => {
          const isToday = new Date().toDateString() === day.toDateString();
          const isCurrentMonth = day.getMonth() === currentDate.getMonth();
          const bg = isToday
            ? 'var(--pf-t--global--background--color--highlight--default, #e7f1fa)'
            : (isCurrentMonth
                ? 'var(--pf-t--global--background--color--primary--default, #ffffff)'
                : 'var(--pf-t--global--background--color--secondary--default, #f0f0f0)');

          // --- NEW: Find holidays for this specific day ---
          // fromIsoDate, not new Date: a stored "2026-01-01" parses as midnight UTC, which
          // in a negative offset is the 31st locally - the holiday would mark the day before.
          const dayHols = activeHolidays.filter(h => fromIsoDate(h.date).toDateString() === day.toDateString());

          return (
            <React.Fragment key={`bg-${dIdx}`}>
              <div style={{ gridColumn: dIdx + 2, gridRow: `1 / ${totalGridRows + 1}`, borderRight: dIdx < 6 ? '1px solid var(--pf-t--global--border--color--default, #d2d2d2)' : 'none', backgroundColor: bg, zIndex: 0 }} />
              
              {/* --- UPDATED: Date Header with Holiday Umbrella --- */}
              <div style={{ gridColumn: dIdx + 2, gridRow: 1, padding: '4px 8px', zIndex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '6px' }}>
                {dayHols.length > 0 && (
                  <Popover
                    headerContent={<div>Holidays</div>}
                    bodyContent={
                      <div style={{ fontSize: '14px' }}>
                        {dayHols.map(h => <div key={h.id}><strong>{h.country.name}:</strong> {h.description}</div>)}
                      </div>
                    }
                  >
                    <Button variant="plain" isInline style={{ padding: 0, color: 'var(--pf-t--global--color--brand--default, #0066cc)' }}>
                      <UmbrellaIcon />
                    </Button>
                  </Popover>
                )}
                <span style={{ fontWeight: isToday ? 'bold' : 'normal', color: isToday ? 'var(--pf-t--global--color--brand--default, #0066cc)' : (isCurrentMonth ? 'inherit' : 'var(--pf-t--global--text--color--subtle, #888)') }}>
                  {day.getDate()}
                </span>
              </div>
            </React.Fragment>
          );
        })}

        {placedEvents.map(({klass, track, startCol, endCol}) => (
          <div key={klass.id} style={{ gridColumn: `${startCol + 2} / span ${endCol - startCol + 1}`, gridRow: track + 2, margin: '2px 4px', zIndex: 10 }}>
            <ClassEventItem klass={klass} isCompactMonth={true} />
          </div>
        ))}

        {!isExpanded && hiddenCount > 0 && (
          <div 
            style={{ gridColumn: '2 / 9', gridRow: displayTracks + 2, fontSize: '12px', fontWeight: 'bold', color: 'var(--pf-t--global--color--brand--default, #0066cc)', padding: '4px', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'color-mix(in srgb, var(--pf-t--global--background--color--secondary--default) 40%, transparent)', borderTop: '1px dashed var(--pf-t--global--border--color--default, #d2d2d2)' }}
            onClick={() => setExpandedWeekIndex(idx)}
          >
            + {hiddenCount} more event{hiddenCount > 1 ? 's' : ''} &#8595;
          </div>
        )}

        {isExpanded && (
          <div 
            style={{ gridColumn: '2 / 9', gridRow: displayTracks + 2, fontSize: '12px', fontWeight: 'bold', color: 'var(--pf-t--global--text--color--subtle, #6a6e73)', padding: '4px', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'color-mix(in srgb, var(--pf-t--global--background--color--secondary--default) 60%, transparent)', borderTop: '1px dashed var(--pf-t--global--border--color--default, #d2d2d2)' }}
            onClick={() => setExpandedWeekIndex(null)}
          >
            Collapse week &#8593;
          </div>
        )}
      </div>
    );
  };

  // What changes which classes the calendar shows comes first - an administrator's only, and
  // absent for everybody else, line and all. Then what decorates it.
  const pageMenuItems: PageActionsMenuItem[] = [
    ...(isAdmin ? [{
      key: 'toggle-non-niit-delivery',
      label: hideNonNiitDelivery ? 'Show non-NIIT deliveries' : 'Hide non-NIIT deliveries',
      onClick: () => setHideNonNiitDelivery(!hideNonNiitDelivery)
    }] : []),
    {
      key: 'manage-holidays',
      hasDividerBefore: isAdmin,
      label: 'Manage Holidays',
      onClick: () => {
        setModalSelectedCountries(selectedHolidayCountries); // Clone current selection
        setIsHolidayModalOpen(true);
      }
    }
  ];

  return (
    <>
      <PageSection variant="default">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <Content>
            <h1>Calendar</h1>
            <p>Manage class deliveries and instructor allocations.</p>
          </Content>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button variant="plain" aria-label="Previous" onClick={handlePrev}><AngleLeftIcon /></Button>
              <Button variant="secondary" onClick={handleToday}>Today</Button>
              <Button variant="plain" aria-label="Next" onClick={handleNext}><AngleRightIcon /></Button>
              
              <Content style={{ marginLeft: '12px', minWidth: '130px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h3 style={{ margin: 0 }}>
                  {viewMode === 'month' 
                    ? currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })
                    : `Starting ${visibleRange.start.toLocaleDateString('default', { month: 'short', day: 'numeric' })}`
                  }
                </h3>
              </Content>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>

              <Switch id="hide-cancelled-switch" label="Hide Cancelled" isChecked={hideCancelled} onChange={(_event, checked) => setHideCancelled(checked)} />

              <ToggleGroup aria-label="Calendar view toggle">
                <ToggleGroupItem text="Week" isSelected={viewMode === 'week'} onChange={() => setViewMode('week')} />
                <ToggleGroupItem text="Month" isSelected={viewMode === 'month'} onChange={() => setViewMode('month')} />
              </ToggleGroup>
            </div>
          </div>

          <div>
            <PageActionsMenu items={pageMenuItems} onRefresh={retry} />
          </div>
        </div>

        {/* Note: The selectedHolidayCountries are deliberately not part of this - they decorate
            the calendar rather than filter it, and are managed from the actions menu. */}
        <Toolbar id="calendar-filter-toolbar" clearAllFilters={clearAllFilters} collapseListedFiltersBreakpoint="xl">
          <ToolbarContent>
            <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
              <ToolbarGroup variant="filter-group" style={{ lineHeight: '22px', alignItems: 'center' }}>
                {buildCategoryDropdown()}
                {buildFilterControls()}
              </ToolbarGroup>
            </ToolbarToggleGroup>
          </ToolbarContent>
        </Toolbar>
      </PageSection>

      <PageSection isFilled>
        <Card isFullHeight>
          <CardBody style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            
            {viewMode === 'month' ? (
              //*** Monthly view ***//
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '40px repeat(7, 1fr)', backgroundColor: 'var(--pf-t--global--background--color--secondary--default, #f0f0f0)', borderBottom: '2px solid var(--pf-t--global--border--color--default, #d2d2d2)' }}>
                  <div style={{ borderRight: '1px solid var(--pf-t--global--border--color--default, #d2d2d2)' }}></div>
                  {weekdays.map(day => <div key={day} style={{ padding: '12px 8px', fontWeight: 'bold', textAlign: 'center' }}>{day}</div>)}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, backgroundColor: 'var(--pf-t--global--border--color--default, #d2d2d2)', overflowY: 'auto' }}>
                  {monthWeeks.map((week, idx) => renderMonthWeekRow(week, idx))}
                </div>
              </>
              ) : (
              // *** Weekly view *** //
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: `repeat(${WEEKS_TO_SHOW}, 1fr)`, 
                gridTemplateRows: `auto repeat(${multiWeekData.displayTracks}, 28px)`, 
                height: '100%',
                backgroundColor: 'var(--pf-t--global--background--color--primary--default, #fff)',
                overflowY: 'auto'
              }}>
                
                {/* 1. Sticky Headers (Row 1) */}
                {weekColumns.map((week, idx) => {
                  const isCurrentWeek = new Date() >= week.start && new Date() <= week.end;
                  
                  const weekHols = activeHolidays.filter(h => {
                    // week.start/end are local midnights, so the holiday has to be read
                    // locally too or a Sunday holiday falls into the previous week.
                    const hd = fromIsoDate(h.date);
                    return hd >= week.start && hd <= week.end;
                  });

                  return (
                    <div key={`header-${week.weekNum}`} style={{ 
                      gridColumn: idx + 1,
                      gridRow: 1,
                      position: 'sticky',
                      top: 0,
                      zIndex: 60, // Above hovered events (50)
                      padding: '12px 8px', 
                      textAlign: 'center', 
                      backgroundColor: isCurrentWeek ? 'var(--pf-t--global--background--color--primary--hover, #e7f1fa)' : 'var(--pf-t--global--background--color--secondary--default, #f0f0f0)', 
                      borderBottom: '2px solid var(--pf-t--global--border--color--default, #d2d2d2)',
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center' 
                    }}>
                      {idx === 0 && <Button variant="plain" aria-label="Previous Week" onClick={handlePrevWeek} style={{ position: 'absolute', left: '0' }}><AngleLeftIcon /></Button>}
                      <div>
                        <div style={{ fontWeight: 'bold', color: isCurrentWeek ? 'var(--pf-t--global--color--brand--default, #0066cc)' : 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          Week {week.weekNum}
                          {weekHols.length > 0 && (
                            <Popover
                              headerContent={<div>Holidays this Week</div>}
                              bodyContent={
                                <div style={{ fontSize: '14px' }}>
                                  {weekHols.map(h => <div key={h.id}><strong>{fromIsoDate(h.date).toLocaleDateString()}:</strong> {h.country.name} - {h.description}</div>)}
                                </div>
                              }
                            >
                              <Button variant="plain" isInline style={{ padding: 0, color: 'var(--pf-t--global--color--brand--default, #0066cc)' }}>
                                <UmbrellaIcon />
                              </Button>
                            </Popover>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--pf-t--global--text--color--subtle, #6a6e73)', fontWeight: 'normal' }}>{week.start.toLocaleDateString('default', { month: 'short', day: 'numeric' })} - {week.end.toLocaleDateString('default', { month: 'short', day: 'numeric' })}</div>
                      </div>
                      {idx === WEEKS_TO_SHOW - 1 && <Button variant="plain" aria-label="Next Week" onClick={handleNextWeek} style={{ position: 'absolute', right: '0' }}><AngleRightIcon /></Button>}
                    </div>
                  );
                })}

                {/* 2. Background Columns (Rows 2 to N) */}
                {weekColumns.map((week, idx) => {
                  const isCurrentWeek = new Date() >= week.start && new Date() <= week.end;
                  return (
                    <div key={`bg-col-${idx}`} style={{ 
                      gridColumn: idx + 1, 
                      gridRow: `2 / span ${multiWeekData.displayTracks}`, 
                      backgroundColor: isCurrentWeek ? 'var(--pf-t--global--background--color--primary--hover, #e7f1fa)' : 'transparent', 
                      borderRight: idx < WEEKS_TO_SHOW - 1 ? '1px solid var(--pf-t--global--border--color--default, #d2d2d2)' : 'none',
                      borderBottom: '1px solid var(--pf-t--global--border--color--default, #d2d2d2)',
                      zIndex: 0 
                    }} />
                  );
                })}

                {/* 3. Event Placements (Offset by +2 to account for Header in Row 1) */}
                {multiWeekData.placements.map(({klass, track, startCol, endCol}) => (
                  <div key={klass.id} style={{ 
                    gridColumn: `${startCol + 1} / span ${endCol - startCol + 1}`, 
                    gridRow: track + 2, 
                    margin: '3px 4px', 
                    zIndex: 10 
                  }}>
                    <ClassEventItem klass={klass} isCompactMonth={true} />
                  </div>
                ))}
              </div>
            )}

          </CardBody>
        </Card>
      </PageSection>
      {/* --- Holiday Management Modal --- */}
      <Modal
        variant={ModalVariant.small}
        isOpen={isHolidayModalOpen}
        onClose={() => setIsHolidayModalOpen(false)}
      >
        <ModalHeader title="Manage Holidays" description="Select the countries whose holidays you want to display on the calendar." />
        <ModalBody>
          <TextInputGroup style={{ marginBottom: '16px' }}>
            <TextInputGroupMain
              placeholder="Search countries..."
              onChange={(_event, val) => setHolidaySearch(val)}
              value={holidaySearch}
            />
            {holidaySearch && (
              <Button 
                variant="plain" 
                onClick={() => setHolidaySearch('')} 
                aria-label="Clear search"
              >
                <TimesIcon /> 
              </Button>
            )}
          </TextInputGroup>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', maxHeight: '300px', overflowY: 'auto' }}>
            {[...countries]
              .filter(c => c.name.toLowerCase().includes(holidaySearch.toLowerCase()))
              .sort((a, b) => {
                const aSel = modalSelectedCountries.includes(a.id.toString());
                const bSel = modalSelectedCountries.includes(b.id.toString());
                if (aSel && !bSel) return -1;
                if (!aSel && bSel) return 1;
                return a.name.localeCompare(b.name);
              })
              .map(country => (
                <Checkbox
                  key={country.id}
                  id={`holiday-country-${country.id}`}
                  label={country.name}
                  isChecked={modalSelectedCountries.includes(country.id.toString())}
                  onChange={() => {
                    const id = country.id.toString();
                    setModalSelectedCountries(prev => 
                      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
                    );
                  }}
                />
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => {
            setSelectedHolidayCountries(modalSelectedCountries); // Sync global state on close
            setIsHolidayModalOpen(false);
          }}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};